/**
 * Utility functions for checking ordering time restrictions
 * Regular days: 11:00 AM to 4:00 AM (Oman time)
 * Friday: 1:00 PM to 4:00 AM (Oman time)
 */

// TESTING: Set to true to force ordering disabled for testing
export const FORCE_ORDERING_DISABLED = false;

// TESTING: Set to "Friday" to simulate Friday, or null to use actual day
// Options: "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday", or null
export const FORCE_DAY_OF_WEEK = null;

// TESTING: Set to a 24h hour number (e.g. 12) to simulate that hour, or null to use actual Oman hour
export const FORCE_HOUR = null;

// Happy Hour window (Oman time): 11:00 AM to 4:00 PM, every day
export const HAPPY_HOUR_START = 11;
export const HAPPY_HOUR_END = 16;

/**
 * Get the current hour (0-23) in Oman timezone, honouring the FORCE_HOUR testing override
 * @returns {number} Current Oman hour
 */
const getOmanHour = () => {
  if (FORCE_HOUR !== null) return FORCE_HOUR;
  const hourFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Muscat",
    hour: "numeric",
    hour12: false
  });
  return parseInt(hourFormatter.format(new Date()), 10);
};

/**
 * Check if ordering is currently allowed based on Oman time
 * @returns {boolean} true if ordering is allowed, false otherwise
 */
export const isOrderingAllowed = () => {
  // For testing: if FORCE_ORDERING_DISABLED is true, always return false
  if (FORCE_ORDERING_DISABLED) {
    return false;
  }

  const now = new Date();

  // Get current day of week and hour in Oman timezone (Asia/Muscat)
  const dayFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Muscat",
    weekday: "long"
  });

  // For testing: use forced day if set, otherwise use actual day
  const dayOfWeek = FORCE_DAY_OF_WEEK || dayFormatter.format(now);
  const omanHour = getOmanHour();

  // Friday: Ordering allowed from 1 PM (13) to 4 AM (03:59)
  if (dayOfWeek === "Friday") {
    return omanHour >= 13 || omanHour < 4;
  }

  // Regular days: Ordering allowed from 11 AM (11) to 4 AM (03:59)
  // This means: hour >= 11 OR hour < 4
  return omanHour >= 11 || omanHour < 4;
};

/**
 * Check if today is a combo deal day (Sat, Sun, Mon, Wed, Thu) in Oman timezone
 * Locked on Tuesdays and Fridays (BOGO days)
 * @returns {boolean} true if combo deal is available today
 */
export const isComboDay = () => {
  const now = new Date();
  const dayFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Muscat",
    weekday: "long"
  });
  const dayOfWeek = FORCE_DAY_OF_WEEK || dayFormatter.format(now);
  return dayOfWeek !== "Tuesday" && dayOfWeek !== "Friday";
};

/**
 * Check if today is Tuesday or Friday in Oman timezone (BOGO offer days)
 * @returns {boolean} true if it is Tuesday or Friday, false otherwise
 */
export const isTuesday = () => {
  const now = new Date();

  // Get current day of week in Oman timezone (Asia/Muscat)
  const dayFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Muscat",
    weekday: "long"
  });

  // For testing: use forced day if set, otherwise use actual day
  const dayOfWeek = FORCE_DAY_OF_WEEK || dayFormatter.format(now);

  return dayOfWeek === "Tuesday" || dayOfWeek === "Friday";
};

/**
 * Check if the Happy Hour lunch treat is currently running.
 * Every day from 11:00 AM to 4:00 PM (Oman time) — 4 PM itself is past the window.
 * @returns {boolean} true if Happy Hour is live right now
 */
export const isHappyHour = () => {
  const omanHour = getOmanHour();
  return omanHour >= HAPPY_HOUR_START && omanHour < HAPPY_HOUR_END;
};

/**
 * Milliseconds left in today's Happy Hour window.
 * @returns {number} ms remaining, or 0 when Happy Hour is not running
 */
export const getHappyHourMsRemaining = () => {
  const hour = getOmanHour();
  if (hour < HAPPY_HOUR_START || hour >= HAPPY_HOUR_END) return 0;

  const partsFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Muscat",
    minute: "2-digit",
    second: "2-digit"
  });
  const parts = partsFormatter.formatToParts(new Date()).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
  const minute = parseInt(parts.minute, 10) || 0;
  const second = parseInt(parts.second, 10) || 0;

  return ((HAPPY_HOUR_END - hour) * 3600 - minute * 60 - second) * 1000;
};

/**
 * Format the Happy Hour time remaining as a short "2h 14m" / "43m" label
 * @returns {string} Empty string when Happy Hour is not running
 */
export const getHappyHourTimeLeftLabel = () => {
  const ms = getHappyHourMsRemaining();
  if (ms <= 0) return '';
  const totalMinutes = Math.ceil(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

/**
 * Get the Happy Hour availability message
 * @returns {string} Formatted Happy Hour window message
 */
export const getHappyHourMessage = () => {
  return "Every day 11:00 AM - 4:00 PM (Oman time)";
};

// TESTING: Set to a "YYYY-MM-DD" string to simulate an Oman date, or null to use the actual date
export const FORCE_DATE = null;

// Omani Women's Day offer: free heart-shaped pizza with any medium/large pizza.
// Whole days, Oman time, inclusive. Keep in sync with WOMENS_DAY_START/END in backend/server.py.
export const WOMENS_DAY_START = "2026-10-17";
export const WOMENS_DAY_END = "2026-10-19";

/**
 * Check if the Omani Women's Day offer is running today (Oman date)
 * @returns {boolean} true on 17, 18 and 19 October 2026
 */
export const isWomensDayOffer = () => {
  // en-CA formats as YYYY-MM-DD, which compares correctly as a string
  const omanDate = FORCE_DATE || new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Muscat",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(new Date());
  return omanDate >= WOMENS_DAY_START && omanDate <= WOMENS_DAY_END;
};

/**
 * How many free heart-shaped pizzas a cart earns today: one per regular (pizza category)
 * medium/large pizza. The backend re-checks this and clamps the customer's choice to it.
 * @param {Array} cartItems Cart items (with category, selectedSize and quantity)
 * @returns {number} 0 when the offer isn't running or nothing qualifies
 */
export const getHeartPizzaAllowance = (cartItems) => getHeartPizzaDefaultFlavours(cartItems).length;

/**
 * Whether a menu item is a regular pizza (pizza category), i.e. can earn or be a heart pizza flavour
 * @param {Object} item Menu or cart item
 * @returns {boolean}
 */
// Exact category names (case-insensitive). Keep in sync with WOMENS_DAY_PIZZA_CATEGORIES in backend/server.py.
// Matched exactly so "PIZZA DIPS" and offer/combo categories never count.
export const PIZZA_CATEGORIES = ['pizza', 'wow special'];

export const isRegularPizza = (item) => {
  const categoryName = (item?.category?.name || item?.category || '').toString().trim().toLowerCase();
  return PIZZA_CATEGORIES.includes(categoryName);
};

/**
 * Default flavour (menu item id) for each free heart pizza the cart earns: heart N gets the
 * flavour of the Nth qualifying pizza, so customers who don't care don't have to pick anything.
 * @param {Array} cartItems Cart items
 * @returns {string[]} One menu item id per free heart pizza; empty when the offer isn't running
 */
export const getHeartPizzaDefaultFlavours = (cartItems) => {
  if (!isWomensDayOffer()) return [];
  return cartItems
    .filter(item => isRegularPizza(item) && ['medium', 'large'].includes(item.selectedSize))
    .flatMap(item => Array(item.quantity).fill(item.id));
};

/**
 * Get current Oman time as a formatted string
 * @returns {string} Formatted time string (e.g., "3:45 PM")
 */
export const getCurrentOmanTime = () => {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Muscat",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
  return formatter.format(now);
};

/**
 * Get ordering hours message - shows all available hours
 * @returns {string} Formatted ordering hours message
 */
export const getOrderingHoursMessage = () => {
  return "Sat-Thu: 11:00 AM - 4:00 AM | Fri: 1:00 PM - 4:00 AM (Oman time)";
};